import { useEffect, useMemo, useRef, useState } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import styled from 'styled-components';

export interface RichEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  height?: number | string;
}

const EditorWrapper = styled.div<{ $height: number | string }>`
  .ql-toolbar {
    border-radius: 8px 8px 0 0;
    border-color: #d9d9d9;
    background: #fafafa;
  }

  .ql-container {
    border-radius: 0 0 8px 8px;
    border-color: #d9d9d9;
    font-size: 14px;
    height: ${(props) =>
      typeof props.$height === 'number'
        ? `${props.$height}px`
        : props.$height || '200px'};
  }

  .ql-editor {
    min-height: ${(props) =>
      typeof props.$height === 'number' ? `${props.$height - 42}px` : '158px'};
  }

  .ql-editor.ql-blank::before {
    color: #bfbfbf;
    font-style: normal;
  }
`;

const ReadOnlyContent = styled.div`
  border-radius: 2px;
  margin-top: 5px;

  * {
    margin: 0;
    font-size: 14px;
    line-height: 22px;
  }

  img {
    max-width: 100%;
    height: auto;
  }

  a {
    color: #1677ff;
  }
`;

const RichEditor = ({
  value,
  onChange,
  placeholder,
  readOnly,
  height = 200,
}: RichEditorProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const quillRef = useRef<Quill | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!containerRef.current || quillRef.current) return;

    quillRef.current = new Quill(containerRef.current, {
      theme: 'snow',
      placeholder: placeholder || 'Enter description...',
      modules: {
        toolbar: [
          ['bold', 'italic', 'underline', 'strike'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ align: [] }],
        ],
      },
    });

    setIsInitialized(true);

    return () => {
      quillRef.current = null;
    };
  }, [placeholder]);

  useEffect(() => {
    if (!quillRef.current || !isInitialized) return;

    const handleTextChange = () => {
      const html = quillRef.current?.root.innerHTML || '';
      const text = quillRef.current?.getText() || '';
      onChange?.(text.trim() ? html : '');
    };

    quillRef.current.on('text-change', handleTextChange);

    return () => {
      quillRef.current?.off('text-change', handleTextChange);
    };
  }, [onChange, isInitialized]);

  useEffect(() => {
    if (!quillRef.current || !isInitialized) return;

    const currentHTML = quillRef.current.root.innerHTML;
    if (value !== currentHTML && value !== undefined) {
      const currentLength = quillRef.current.getLength();
      quillRef.current.clipboard.dangerouslyPasteHTML(value);
    }
  }, [value, isInitialized]);

  useEffect(() => {
    if (!quillRef.current || !isInitialized) return;

    quillRef.current.enable(!readOnly);
  }, [readOnly, isInitialized]);

  const renderedContent = useMemo(() => {
    if (!value) return null;
    return <div dangerouslySetInnerHTML={{ __html: value }} />;
  }, [value]);

  if (readOnly) {
    return (
      <ReadOnlyContent>
        {value ? renderedContent : <span style={{ color: '#bfbfbf' }}>-</span>}
      </ReadOnlyContent>
    );
  }

  return <EditorWrapper ref={containerRef} $height={height} />;
};

export default RichEditor;
